import dayjs from 'dayjs';

// Сколько дней без движения считается «зависанием»
export const STALE_DAYS = 7;

// Порядок флажков «Моих задач»: квадранты Эйзенхауэра
export const PULSE_PRIORITIES = [1, 2, 3, 4];

// Только незакрытые задачи с оценкой (оба признака заданы)
export const ratedOpen = (tasks) => (tasks ?? [])
    .filter((task) => !task.isClosed && PULSE_PRIORITIES.includes(task.priority));

const LEVELS = ['red', 'yellow', 'green', 'idle'];

// Полных дней от даты до сегодня (будущее — 0), null — даты нет
export const daysSince = (value, now = dayjs()) => {
    if (!value) return null;
    const date = dayjs(value);
    if (!date.isValid()) return null;
    return Math.max(0, now.startOf('day').diff(date.startOf('day'), 'day'));
};

export const isStale = (value, now = dayjs()) => {
    const days = daysSince(value, now);
    return days == null || days >= STALE_DAYS;
};

export const isReleaseOverdue = (row, now = dayjs()) => !!row.nextVersionDate && row.nextVersionOpen > 0
    && dayjs(row.nextVersionDate).isBefore(now.startOf('day'));

// Здоровье строки проекта или сотрудника: red — просрочка или сорван релиз, yellow — нет движения
// STALE_DAYS+ дней или строка-«сирота» (orphanReason), green — в порядке, idle — нет открытых задач
export const rowHealth = (row, now = dayjs(), orphanReason = null) => {
    if (!row.qtyOpen) return { level: 'idle', reason: 'Нет открытых задач' };
    if (row.qtyOld > 0) return { level: 'red', reason: `Просрочено задач: ${row.qtyOld}` };
    if (isReleaseOverdue(row, now)) return { level: 'red', reason: `Релиз ${row.nextVersionName} просрочен` };
    if (orphanReason) return { level: 'yellow', reason: orphanReason };
    const days = daysSince(row.lastActivity, now);
    if (days == null) return { level: 'yellow', reason: 'Нет движения' };
    if (days >= STALE_DAYS) return { level: 'yellow', reason: `Нет движения ${days} дн` };
    return { level: 'green', reason: 'В порядке' };
};

// Строки с вычисленным здоровьем: проблемные сверху, затем больше просрочки и открытых, затем по имени
export const rankRows = (rows, now = dayjs(), orphanOf = () => null) => (rows ?? [])
    .map((row) => ({ ...row, health: rowHealth(row, now, orphanOf(row)) }))
    .sort((a, b) => LEVELS.indexOf(a.health.level) - LEVELS.indexOf(b.health.level)
        || b.qtyOld - a.qtyOld
        || b.qtyOpen - a.qtyOpen
        || (a.name || '').localeCompare(b.name || '', 'ru')
        || (a.companyName || '').localeCompare(b.companyName || '', 'ru'));

export const activityLabel = (value, now = dayjs()) => {
    const days = daysSince(value, now);
    if (days == null) return '—';
    if (days === 0) return 'сегодня';
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дн назад`;
    if (days < 30) return `${Math.floor(days / 7)} нед назад`;
    if (days < 365) return `${Math.floor(days / 30)} мес назад`;
    return 'больше года назад';
};

// 'overdue' — срок прошёл, 'today' — срок сегодня, null — закрыта, без срока или срок впереди
export const dueState = (task, now = dayjs()) => {
    if (task.isClosed || !task.planDate) return null;
    const date = dayjs(task.planDate).startOf('day');
    const today = now.startOf('day');
    if (date.isBefore(today)) return 'overdue';
    return date.isSame(today) ? 'today' : null;
};

// Счётчики по квадрантам; задачи без оценки не учитываются
export const priorityCounts = (tasks) => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };
    (tasks ?? []).forEach((task) => {
        if (counts[task.priority] !== undefined) counts[task.priority] += 1;
    });
    return counts;
};

// По умолчанию — первый непустой квадрант, начиная с «Сделать сейчас»
export const defaultPriority = (counts) => PULSE_PRIORITIES.find((value) => counts[value] > 0) ?? 1;

// Чипы фирм: с задачами (открытыми или закрытыми за 7 дней) и выбранная; остальные — в «ещё N без задач»
export const splitCompanies = (companies, selectedId = 0) => {
    const shown = [];
    const hidden = [];
    (companies ?? []).forEach((company) => {
        const busy = company.qtyOpen > 0 || company.qtyDone7 > 0 || company.companyId === selectedId;
        (busy ? shown : hidden).push(company);
    });
    return { shown, hidden };
};
